/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import colors from '@sportninja/common/constants/appColors'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { LockOutlined, LinkOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import RosterSelectNew from 'src/components/List/RosterSelectNew'
import { Flex } from 'src/components/Layout'
import EditEntitySubTitle from 'src/pages/EditEntitySubTitle'
import { useUrlManager } from 'src/hooks/useUrlManager'
import { useRosterContext } from '../context/RosterContext'

interface RosterHeaderProps {
  teamId: string
  teamName: string
  permission: Record<string, any>
}

const RosterHeader = ({ teamId, teamName, permission }: RosterHeaderProps) => {
  const { setParam } = useUrlManager()
  const {
    rosters,
    selectedRosterId,
    setSelectedRosterId,
    lockRosterDate,
    permissions,
    loadRosters,
  } = useRosterContext()

  const selectedRoster = useMemo(
    () => rosters.find((r: any) => r.id === selectedRosterId),
    [rosters, selectedRosterId]
  )

  return (
    <div
      css={css`
        display: flex;
        justify-content: ${rosters.length === 0 ? 'center' : 'space-between'};
        align-items: center;
        flex-wrap: wrap;
        ${rosters.length === 0 &&
        css`
          min-height: 200px;
          width: 100%;
        `}
      `}
    >
      <Flex
        // @ts-ignore
        alignItems='center'
        justifyContent={rosters.length === 0 ? 'center' : 'flex-start'}
        css={css`
          gap: 20px;
          ${rosters.length === 0 &&
          css`
            width: 100%;
          `}
        `}
      >
        <RosterSelectNew
          selectedRosterId={selectedRosterId}
          setSelectedRosterId={setSelectedRosterId}
          rosters={rosters}
          loadRosters={loadRosters}
          teamId={teamId}
          teamName={teamName}
          permission={permission}
          // @ts-ignore
          setParam={setParam}
        />
        {rosters.length > 0 && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              height: 40px;
              gap: 8px;
              margin-bottom: 8px;
            `}
          >
            <Link
              to={`/schedule/${
                selectedRoster?.schedule_uid || rosters[0]?.schedule_uid
              }`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <p
                css={css`
                  font-size: 24px;
                  font-weight: 700;
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                  line-height: 32px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {selectedRoster?.schedule_name || rosters[0]?.schedule_name}
              </p>
              <LinkOutlined
                css={css`
                  font-size: 24px;
                  color: ${colors.PRIMARY};
                `}
              />
            </Link>
            <p
              css={css`
                color: ${colors.NEUTRAL_50};
              `}
            >
              {selectedRoster?.division_name || rosters[0]?.division_name}
            </p>
          </div>
        )}
      </Flex>
      {selectedRosterId ? (
        <div
          css={css`
            margin-left: 24px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: 16px;
          `}
        >
          {lockRosterDate && (permissions?.update || permissions?.admin) && (
            <div
              css={css`
                display: flex;
                padding: 8px;
                align-items: center;
                gap: 8px;
                border-radius: 8px;
                background: ${colors.SECONDARY_300};
                align-self: center;
              `}
            >
              <LockOutlined
                css={css`
                  font-size: 24px;
                  color: ${colors.NEUTRAL_50};
                `}
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                `}
              >
                <p
                  css={css`
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 16px;
                    color: ${colors.NEUTRAL_50};
                  `}
                >
                  Roster Lock Date
                </p>
                <p
                  css={css`
                    font-size: 14px;
                    line-height: 20px;
                    font-weight: 700;
                    color: ${colors.WHITE};
                  `}
                >
                  {dayjs(lockRosterDate).format('DD MMM, YYYY')}
                </p>
              </div>
            </div>
          )}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              align-self: center;
              margin-bottom: 16px;
            `}
          >
            <EditEntitySubTitle
              id={teamId}
              entityType={ENTITY_TYPES.team}
              fixedWidth
            />
            <EditEntitySubTitle
              id={selectedRosterId}
              entityType={ENTITY_TYPES.roster}
              fixedWidth
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default RosterHeader
